/**
 * Custom React hook for converting Excel (.xlsx) files to JSON format.
 *
 * This hook utilizes the `xlsx` library to read and parse Excel files
 * directly in the browser, converting the first sheet of the Excel file
 * into a JSON array. Each element in the returned array corresponds to a
 * row in the Excel sheet, with object keys as column headers.
 *
 * Usage:
 * 1. Import the hook into your component.
 * 2. Call the hook to get access to the `xlsxToJson` function.
 * 3. Pass a File object (from a file input element, for example) to `xlsxToJson`.
 * 4. `xlsxToJson` returns a Promise that resolves with the JSON representation
 *    of the Excel file or rejects if no file is provided.
 *
 * Example:
 * ```
 * const { xlsxToJson } = useXlsxToJson();
 * xlsxToJson(file).then(json => console.log(json)).catch(error => console.error(error));
 * ```
 *
 * Note:
 * - Only the first sheet of the Excel file is processed.
 * - The function expects a File object as its parameter, which can be obtained
 *   from an input element of type "file".
 * - The function returns a Promise, allowing for asynchronous handling of file
 *   reading and parsing operations.
 */
import * as XLSX from "xlsx";

const useXlsxToJson = () => {
  /**
   * Converts an Excel file to JSON.
   *
   * @param {File} file - The Excel (.xlsx) file to be converted.
   * @returns {Promise<Array<Object>>} A promise that resolves with the JSON array.
   */
  const xlsxToJson = (file) => {
    return new Promise((resolve, reject) => {
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          // Read the first sheet from the workbook
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          // Convert the sheet to JSON
          const json = XLSX.utils.sheet_to_json(worksheet);
          resolve(json);
        };
        reader.readAsArrayBuffer(file);
      } else {
        reject("No file provided");
      }
    });
  };

  return { xlsxToJson };
};

export default useXlsxToJson;
